// address.tsx
// app/components/AddressCard.tsx
'use client';

import React, { useState } from 'react';
import { FaMapMarkerAlt, FaDirections, FaPhone, FaGlobe, FaRegClock, FaUser, FaCalendarAlt, FaEye, FaEnvelope } from 'react-icons/fa';
import { Address } from '../../types';
import { useReviewerAuth } from '../../contexts/ReviewerAuthContext';

interface AddressCardProps {
  address: Address;
  phoneNumber: string;
  website: string;
  hosts: string[];
  openingHours: string;
  campsiteName: string;
  booking_url?: string;
  entityId?: number;
  entityName?: string;
  campsiteType?: string;
  managedBy?: string;
  managedByType?: string;
  email?: string;
}

function trackPhoneReveal(entityId?: number, entityName?: string, campsiteType?: string) {
  try {
    const payload = JSON.stringify({
      clickType: 'phone',
      entityId,
      entityName: entityName || '',
      campsiteType: campsiteType || '',
    });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(
        'https://app.trip-planner.com.au/api/products/track-booking-click',
        new Blob([payload], { type: 'application/json' }),
      );
    } else {
      fetch('https://app.trip-planner.com.au/api/products/track-booking-click', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    // Never block the user
  }
}

export default function AddressCard({
  address,
  phoneNumber,
  website,
  hosts,
  openingHours,
  campsiteName,
  booking_url,
  entityId,
  entityName,
  campsiteType,
  managedBy,
  managedByType,
  email,
}: AddressCardProps) {
  const [phoneRevealed, setPhoneRevealed] = useState(false);
  const { isLoggedIn, login } = useReviewerAuth();
  const addressString = [address.street, address.street2, address.town, address.state, address.postcode]
    .filter(Boolean).join(', ');
  // Locality line built from defined parts only — feed-sourced free camps have
  // just a state (no street/town/postcode), so the old template printed
  // "undefined, NSW undefined".
  const localityLine = [
    [address.town, address.state].filter(Boolean).join(', '),
    address.postcode,
  ].filter(Boolean).join(' ') || 'Australia';

  return (
    <div className="ca-side-address bg-white rounded-lg shadow-md p-6">
      <div className="flex items-start mb-4">
        <FaMapMarkerAlt className="text-red-500 mr-2 mt-1 flex-shrink-0" />
        <div>
          {address.street && <p className="font-semibold">{address.street}</p>}
          {address.street2 && <p>{address.street2}</p>}
          <p>{localityLine}</p>
        </div>
      </div>

      {/* Map moved to a large main-column section (LocationMapSection); the
          sidebar keeps the address + actions to avoid a duplicate small map. */}

      <div className="flex flex-col gap-2 mb-4">
        <a
          href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addressString)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center bg-[#1558b0] text-white py-3 px-4 rounded-md hover:bg-[#1558b0] transition-colors duration-300 font-semibold text-lg"
        >
          <FaDirections className="mr-4" />
          <span>Get Directions</span>
        </a>
        
        {booking_url && booking_url.trim() !== '' && (
          <a
            href={booking_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center bg-green-600 text-white py-3 px-4 rounded-md hover:bg-green-700 transition-colors duration-300 font-semibold text-lg"
          >
            <FaCalendarAlt className="mr-4" />
            <span>Book Now</span>
          </a>
        )}
      </div>
      
      <div className="mt-4">
        {phoneNumber && (
          <div className="flex items-center mb-2">
            <FaPhone className="mr-2 text-gray-600 flex-shrink-0" />
            {phoneRevealed ? (
              <a href={`tel:${phoneNumber}`} className="text-[#1558b0] hover:underline font-medium">{phoneNumber}</a>
            ) : (
              <button
                onClick={() => {
                  setPhoneRevealed(true);
                  trackPhoneReveal(entityId, entityName, campsiteType);
                }}
                className="inline-flex items-center gap-1.5 text-[#1558b0] hover:text-[#123f7d] font-medium transition-colors"
              >
                <FaEye className="text-sm" />
                <span>Reveal Phone Number</span>
              </button>
            )}
          </div>
        )}
        {phoneRevealed && !isLoggedIn && (
          <div className="ml-6 mb-2">
            <button
              onClick={() => {
                login('google').catch(() => {
                  // Fallback: open login URL directly in the browser
                  window.location.href = `https://app.trip-planner.com.au/api/reviewer/auth/google/login?return_url=${encodeURIComponent(window.location.href)}`;
                });
              }}
              className="text-xs text-[#1558b0] hover:text-[#123f7d] hover:underline transition-colors"
            >
              Create a free account for trip planning features &rarr;
            </button>
          </div>
        )}
        {email && (
          <p className="flex items-center mb-2">
            <FaEnvelope className="mr-2 text-gray-600 flex-shrink-0" />
            <a href={`mailto:${email}`} className="text-[#1558b0] hover:underline">{email}</a>
          </p>
        )}
        {website && (
          <p className="flex items-center mb-2">
            <FaGlobe className="mr-2 text-gray-600" />
            <a href={`https://${website.replace(/(^\w+:|^)\/\//, '')}`} target="_blank" rel="noopener noreferrer" className="text-[#1558b0] hover:underline">
              Website
            </a>
          </p>
        )}
        {openingHours && (
          <p className="flex items-center mb-2">
            <FaRegClock className="mr-2 text-gray-600" />
            {openingHours}
          </p>
        )}
      </div>
      
      {hosts && hosts.length > 0 && (
        <div className="mt-4">
          <h3 className="font-semibold mb-2 flex items-center">
            <FaUser className="mr-2 text-gray-600" />
            Hosts:
          </h3>
          <ul>
            {hosts.map((host, index) => (
              <li key={index}>{host}</li>
            ))}
          </ul>
        </div>
      )}
      {managedBy && (
        <div className="mt-4 pt-3 border-t border-gray-100">
          <p className="flex items-center text-sm text-gray-700">
            <FaUser className="mr-2 text-gray-500 flex-shrink-0" />
            <span>
              Managed by <span className="font-semibold">{managedBy}</span>
            </span>
          </p>
          {managedByType === 'traditional_owners' && (
            <p className="ml-6 mt-1 text-xs text-emerald-700 font-medium">Traditional Owners</p>
          )}
        </div>
      )}
    </div>
  );
}